import { SEO_PAGES } from '../content/seoPages.js';

export const PUBLIC_ORIGIN = 'https://laudando.online';

const normalizePath = (pathname = '/') => {
  const raw = String(pathname || '/').trim();
  if (!raw.startsWith('/')) return `/${raw}`.replace(/\/+$/, '') || '/';
  return raw.replace(/\/+$/, '') || '/';
};

const normalizeKeyword = (value = '') => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const veterinaryPattern = /\b(veterinari|veterinário|veterinaria|veterinária|canino|felino|animal)\b/i;

export const buildSeoPageCanonical = (path) => {
  const normalized = normalizePath(path);
  if (normalized.includes('?') || normalized.includes('#')) throw new Error('Canonical SEO path cannot contain query/hash');
  return `${PUBLIC_ORIGIN}${normalized}`;
};

export const buildSeoPageMeta = (page) => {
  if (!page) return null;
  return {
    title: page.title,
    description: page.meta_description,
    canonical: buildSeoPageCanonical(page.path),
    robots: page.index_status === 'index' ? 'index,follow' : 'noindex,follow',
  };
};

export const getIndexableSeoPages = (pages = SEO_PAGES) =>
  (pages || []).filter((page) => page?.index_status === 'index');

export const getSeoPageByPath = (pathname) => {
  const normalized = normalizePath(pathname);
  return SEO_PAGES.find((page) => normalizePath(page.path) === normalized) || null;
};

export const validateSeoPageRegistry = (pages = SEO_PAGES) => {
  const errors = [];
  const paths = new Map();
  const owners = new Map();

  for (const [index, page] of (pages || []).entries()) {
    const label = `page[${index}]`;
    const path = String(page?.path || '');
    const normalizedPath = normalizePath(path);
    const keyword = normalizeKeyword(page?.primary_keyword);
    const intent = String(page?.intent || '').trim().toLowerCase();

    if (!path.startsWith('/')) errors.push(`${label}: path must start with /`);
    if (path.includes('?') || path.includes('#')) errors.push(`${label}: canonical path cannot contain query/hash`);
    if (!page?.primary_keyword) errors.push(`${label}: primary_keyword is required`);
    if (!page?.intent) errors.push(`${label}: intent is required`);

    if (paths.has(normalizedPath)) errors.push(`${label}: duplicate path ${normalizedPath}`);
    else paths.set(normalizedPath, index);

    if (keyword && intent && page?.index_status === 'index') {
      const ownerKey = `${intent}::${keyword}`;
      if (owners.has(ownerKey)) errors.push(`${label}: duplicate keyword ownership for ${page.primary_keyword} (${intent})`);
      else owners.set(ownerKey, index);
    }

    const veterinaryHaystack = [page?.topic, page?.modality, page?.primary_keyword, ...(page?.secondary_keywords || [])].filter(Boolean).join(' ');
    if (page?.modality === 'veterinaria' || veterinaryPattern.test(veterinaryHaystack)) {
      errors.push(`${label}: veterinary content cannot appear in medical-human SEO registry`);
    }

    if (page?.index_status === 'index') {
      for (const field of ['title', 'meta_description', 'h1', 'summary']) {
        if (!String(page?.[field] || '').trim()) errors.push(`${label}: ${field} is required for indexable pages`);
      }
    }
  }

  return { ok: errors.length === 0, errors };
};

const registryValidation = validateSeoPageRegistry(SEO_PAGES);
if (!registryValidation.ok) {
  throw new Error(`Invalid SEO page registry:\n${registryValidation.errors.join('\n')}`);
}
